import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import { Email, EmailProps } from './email/email';

export interface NotificationEmailsProps {
  data: EmailProps[];
  status: LoadingState;
}

export const NotificationEmails = types
  .model({
    data: types.optional(types.array(Email), []),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: NotificationEmailsProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
    setNotificationEmails(emailList: EmailProps[]): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        data: [...emailList],
      });
    },
  }))
  .actions((self) => ({
    async setNotificationEmailsStore(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.settings.notificationEmails,
      });
    },
    removeNotificationEmail(selectedId: number): void {
      self.setNotificationEmails([
        ...getSnapshot(self.data).filter(({ id }) => id !== selectedId),
      ]);
    },
    addNotificationEmail(): void {
      const dataSnap = getSnapshot(self.data);
      self.setNotificationEmails([
        ...dataSnap,
        { id: dataSnap[dataSnap.length - 1].id + 1, email: '' },
      ]);
    },
    updateNotificationEmail(changedField: EmailProps): void {
      const dataSnap = getSnapshot(self.data);
      self.setNotificationEmails([
        ...dataSnap.map(({ id, email }) => ({
          id,
          email: id === changedField.id ? changedField.email : email,
        })),
      ]);
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setNotificationEmailsStore();
    },
    async update(): Promise<void> {
      await self.setNotificationEmailsStore();
    },
  }))
  .views((self) => ({
    getNotificationEmailsRows(): EmailProps[] {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const notificationEmails = NotificationEmails.create();
export type NotificationEmailsSnapshot = SnapshotIn<typeof NotificationEmails>;
export type NotificationEmailsInstance = Instance<typeof NotificationEmails>;
export default notificationEmails;
